import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import { TabButton, TabGroup } from '../../ui/Tabs/index.js';
import { PhaseCompositionBar } from './PhaseCompositionBar.js';
import { PhaseSegmentTable } from './PhaseSegmentTable.js';
import { WaitTimePanel } from './WaitTimePanel.js';
import { formatQuantile, sortPmoFlowSegments, type PmoFlowValueMode } from './segment-utils.js';
import type {
  PmoFlowCohortSummary,
  PmoFlowSegmentKey,
  PmoFlowSummaryResponse,
} from '../../../types/pmo-flow.js';

/**
 * バーと一覧を突き合わせるハイライト対象。母集団もキーに含めるので、
 * 「デザインあり」のバーに hover したとき光るのは同じ母集団の行だけになる
 */
interface HighlightTarget {
  scope: string;
  key: PmoFlowSegmentKey;
}

export interface PhaseCompositionCardProps {
  /** サマリーレスポンス（未取得なら undefined） */
  data: PmoFlowSummaryResponse | undefined;
  /** 読み込み状態 */
  isLoading: boolean;
}

/**
 * 母集団の見出し（母集団名 + 母票数、任意で全体リードタイム）。
 * バー側と一覧側で同じ見出しを使い、どちらのブロックがどの母集団か迷わせない。
 * 母集団名は短く保ち、定義（デザインチェック子票の有無）は title 属性に逃がす。
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business PMOフェーズ構成の母集団見出し（母集団名・母票数・全体リードタイム）を描画する
 * @graph-connects none
 */
function CohortHeading({
  cohort,
  withLeadTime = false,
}: {
  cohort: PmoFlowCohortSummary;
  /** バー側だけ全体リードタイムを添える（一覧側は各行に分位数が出るので省く） */
  withLeadTime?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
      <span className="flex items-center gap-2">
        <span
          className="text-sm font-semibold text-[var(--color-text-primary)]"
          title={t(`kpi.pmoFlow.cohort.${cohort.key}Hint`)}
        >
          {t(`kpi.pmoFlow.cohort.${cohort.key}`)}
        </span>
        <span className="rounded-full bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[11px] font-medium tabular-nums text-[var(--color-text-secondary)]">
          {t('kpi.pmoFlow.cohort.count', { count: cohort.usableCount })}
        </span>
      </span>
      {withLeadTime && (
        <span className="text-xs text-[var(--color-text-muted)]">
          {t('kpi.pmoFlow.cohort.leadTimeLabel')}{' '}
          <span className="font-semibold tabular-nums text-[var(--color-text-secondary)]">
            {formatQuantile(cohort.totalLeadTime?.p50)}
          </span>
          {t('kpi.pmoFlow.unit.days')}
        </span>
      )}
    </div>
  );
}

/**
 * 母集団 1 つ分のバーブロック。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMOフェーズ構成を母集団ごとに見出し付きのバーとして描画する
 * @graph-connects PhaseCompositionBar [calls] 横積み上げバー描画
 */
function CohortBar({
  cohort,
  isLoading,
  highlight,
  onHighlight,
}: {
  cohort: PmoFlowCohortSummary;
  isLoading: boolean;
  highlight: HighlightTarget | null;
  onHighlight: (target: HighlightTarget | null) => void;
}) {
  return (
    <div className="mb-5 last:mb-0" data-testid={`pmo-flow-cohort-bar-${cohort.key}`}>
      <CohortHeading cohort={cohort} withLeadTime />
      <PhaseCompositionBar
        segments={sortPmoFlowSegments(cohort.segments)}
        isLoading={isLoading}
        highlightedKey={highlight?.scope === cohort.key ? highlight.key : null}
        onHighlight={(key) => {
          onHighlight(key === null ? null : { scope: cohort.key, key });
        }}
      />
      <WaitTimePanel waitTotal={cohort.waitTotal} />
    </div>
  );
}

/**
 * 母集団 1 つ分の区間別一覧。バーと同じ母集団の切り口で並べ、左右に並べて比較する。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMOフェーズ区間別の件数・分位数一覧を母集団ごとに描画する
 * @graph-connects PhaseSegmentTable [calls] 区間別一覧描画
 */
function CohortTable({
  cohort,
  mode,
  highlight,
  onHighlight,
}: {
  cohort: PmoFlowCohortSummary;
  mode: PmoFlowValueMode;
  highlight: HighlightTarget | null;
  onHighlight: (target: HighlightTarget | null) => void;
}) {
  return (
    <div data-testid={`pmo-flow-cohort-table-${cohort.key}`}>
      <CohortHeading cohort={cohort} />
      <PhaseSegmentTable
        segments={sortPmoFlowSegments(cohort.segments)}
        mode={mode}
        workTotal={cohort.workTotal}
        projectTotal={cohort.projectTotal}
        highlightedKey={highlight?.scope === cohort.key ? highlight.key : null}
        onHighlight={(key) => {
          onHighlight(key === null ? null : { scope: cohort.key, key });
        }}
      />
    </div>
  );
}

/**
 * フェーズ構成カード（この画面の主役）。上段に母集団（デザインあり / 開発のみ）別の
 * 横積み上げバーを縦に並べ、下段に同じ母集団別の区間一覧を左右に並べる。
 * バーは要件定義（開発 assign より前・淡い緑）から始まり、assign 以降は待ち＝青 /
 * 作業＝橙 で続く。見出しの全体リードタイムは assign→結合テスト完了 のままで、
 * 要件定義 2 区間を含まない（title 属性でその口径を明示する）。
 * 実日数と pt 正規化（pt/日）はピル型スイッチ（TabGroup）で切り替える。
 * 計測できていない区間は淡い破線のプレースホルダーとして構成の中に残す。
 *
 * バーの区間と一覧の行は双方向に連動する。片方に hover すると同じ母集団・同じ区間の
 * もう片方が光るので、バーのどの帯がどの数字なのかを目で辿れる。母集団をまたいでは
 * 光らせない（比較対象ではなく別々の母数のため）。
 *
 * バーの下には案件あたりの待ち合計を別スケールで添える。待ちの p90 は構成バーの全長
 * （リードタイム中央値）を超えるため同じ軸には載せられず、かつ改善余地は中央値ではなく
 * その尾の側にあるので、専用のパネルとして分ける。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMOフェーズリードタイムの主要カード。母集団別の横積み上げバーと区間別分位数一覧を表示し、実日数/pt正規化を切り替え、未計測区間をプレースホルダーとして明示する
 * @graph-connects CohortBar [calls] 母集団別バー描画
 * @graph-connects WaitTimePanel [calls] 母集団別の待ち合計パネル描画
 * @graph-connects CohortTable [calls] 母集団別一覧描画
 * @graph-connects PhaseCompositionBar [calls] 横積み上げバー描画
 * @graph-connects TabGroup [calls] 実日数/pt正規化スイッチ描画
 * @graph-connects Card [calls] カードコンポーネント描画
 */
export function PhaseCompositionCard({ data, isLoading }: PhaseCompositionCardProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<PmoFlowValueMode>('days');
  const [highlight, setHighlight] = useState<HighlightTarget | null>(null);

  const segments = data ? sortPmoFlowSegments(data.segments) : [];
  const cohorts = data?.cohorts ?? [];
  const pendingCount = segments.filter((segment) => segment.availability === 'pending').length;

  return (
    <Card className="animate-content-reveal">
      <CardHeader
        title={t('kpi.pmoFlow.composition.title')}
        description={t('kpi.pmoFlow.composition.description')}
        action={
          data?.totalLeadTime ? (
            <div className="text-right">
              <p
                className="text-sm text-[var(--color-text-muted)]"
                title={t('kpi.pmoFlow.composition.totalLeadTimeHint')}
              >
                {t('kpi.pmoFlow.composition.totalLeadTime')}
              </p>
              <p className="text-xl font-bold tabular-nums text-[var(--color-text-primary)]">
                {formatQuantile(data.totalLeadTime.p50)}
                <span className="ml-1 text-xs font-normal text-[var(--color-text-muted)]">
                  {t('kpi.pmoFlow.unit.days')}
                </span>
              </p>
            </div>
          ) : undefined
        }
      />
      <CardContent>
        {isLoading || cohorts.length === 0 ? (
          <PhaseCompositionBar segments={segments} isLoading={isLoading} />
        ) : (
          cohorts.map((cohort) => (
            <CohortBar
              key={cohort.key}
              cohort={cohort}
              isLoading={isLoading}
              highlight={highlight}
              onHighlight={setHighlight}
            />
          ))
        )}

        {pendingCount > 0 && (
          <p className="mt-2 text-xs text-[var(--color-text-muted)]">
            {t('kpi.pmoFlow.composition.notToScaleNote', { value: pendingCount })}
          </p>
        )}

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-sm font-medium text-[var(--color-text-primary)]">
              {t('kpi.pmoFlow.table.title')}
            </p>
            {mode === 'perPoint' && (
              <p className="mt-0.5 max-w-2xl text-xs text-[var(--color-text-muted)]">
                {t('kpi.pmoFlow.table.perPointScopeNote')}
              </p>
            )}
          </div>
          {/* 注記が伸びてもスイッチのラベルが折り返さないよう、切替側は縮めない */}
          <div className="shrink-0">
            <TabGroup ariaLabel={t('kpi.pmoFlow.viewSwitch.label')}>
              <TabButton
                active={mode === 'days'}
                label={t('kpi.pmoFlow.viewSwitch.days')}
                onClick={() => {
                  setMode('days');
                }}
              />
              <TabButton
                active={mode === 'perPoint'}
                label={t('kpi.pmoFlow.viewSwitch.perPoint')}
                onClick={() => {
                  setMode('perPoint');
                }}
              />
            </TabGroup>
          </div>
        </div>

        {/* バーと同じ母集団の切り口で左右に並べる。混ぜると片方の母数（デザインあり 48 件）が
            もう片方（開発のみ 356 件）に埋もれて、区間ごとの信頼度が読めなくなる */}
        <div className="mt-3 grid gap-x-6 gap-y-5 lg:grid-cols-2">
          {isLoading || cohorts.length === 0 ? (
            <div className="h-64 animate-pulse rounded-lg bg-[var(--color-bg-elevated)] lg:col-span-2" />
          ) : (
            cohorts.map((cohort) => (
              <CohortTable
                key={cohort.key}
                cohort={cohort}
                mode={mode}
                highlight={highlight}
                onHighlight={setHighlight}
              />
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
